import React, { useState, useEffect, useCallback } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import MessageEditor from "./MessageEditor";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * Approval Dashboard for reviewing, editing, approving, and rejecting
 * AI-generated simulation messages.
 */
const ApprovalDashboard = ({ onApprovalChange }) => {
  const [batches, setBatches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [channelFilter, setChannelFilter] = useState("ALL");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editingMessage, setEditingMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hideApproved, setHideApproved] = useState(false);

  const fetchBatches = useCallback(async () => {
    try {
      const data = await SimulationDataGeneratorAPI.getBatchSummaries();
      setBatches(data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (selectedBatch) {
        data = await SimulationDataGeneratorAPI.getPendingBatch(selectedBatch);
      } else {
        const channel = channelFilter !== "ALL" ? channelFilter : null;
        const status = statusFilter !== "ALL" ? statusFilter : null;
        data = await SimulationDataGeneratorAPI.getPendingMessages(
          channel,
          status,
        );
      }
      setMessages(data || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [selectedBatch, channelFilter, statusFilter]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const refresh = async () => {
    await Promise.all([fetchBatches(), fetchMessages()]);
    setSelectedIds(new Set());
    if (onApprovalChange) onApprovalChange();
  };

  // ── Approval/Rejection Actions ──

  const handleApprove = async (uuid) => {
    try {
      await SimulationDataGeneratorAPI.approveMessages([uuid]);
      await refresh();
    } catch (err) {
      setError("Failed to approve message: " + (err.message || ""));
    }
  };

  const handleReject = async (uuid) => {
    try {
      await SimulationDataGeneratorAPI.rejectMessages([uuid], rejectReason);
      setRejectingId(null);
      setRejectReason("");
      await refresh();
    } catch (err) {
      setError("Failed to reject message: " + (err.message || ""));
    }
  };

  const handleApproveBatch = async (batchId) => {
    try {
      await SimulationDataGeneratorAPI.approveBatch(batchId);
      await refresh();
    } catch (err) {
      setError("Failed to approve batch: " + (err.message || ""));
    }
  };

  const handleRejectBatch = async (batchId) => {
    try {
      await SimulationDataGeneratorAPI.rejectBatch(batchId, null);
      await refresh();
    } catch (err) {
      setError("Failed to reject batch: " + (err.message || ""));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    try {
      await SimulationDataGeneratorAPI.approveMessages([...selectedIds]);
      await refresh();
    } catch (err) {
      setError("Failed to bulk approve: " + (err.message || ""));
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0) return;
    try {
      await SimulationDataGeneratorAPI.rejectMessages([...selectedIds], null);
      await refresh();
    } catch (err) {
      setError("Failed to bulk reject: " + (err.message || ""));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      for (const id of selectedIds) {
        await SimulationDataGeneratorAPI.deletePendingMessage(id);
      }
      await refresh();
    } catch (err) {
      setError("Failed to bulk delete: " + (err.message || ""));
    }
  };

  // ── Editing ──

  const handleSaveEdit = async (uuid, updatedData) => {
    try {
      await SimulationDataGeneratorAPI.updatePendingMessage(uuid, updatedData);
      setEditingMessage(null);
      await refresh();
    } catch (err) {
      setError("Failed to save edit: " + (err.message || ""));
    }
  };

  // ── Selection Helpers ──

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMessages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMessages.map((m) => m.uuid)));
    }
  };

  // ── Filtering ──

  const normalizeChannel = (ch) => (ch || "").toUpperCase().replace(/-/g, "_");

  const filteredMessages = messages.filter((msg) => {
    if (hideApproved && msg.approvalStatus === "APPROVED") return false;
    if (statusFilter !== "ALL" && msg.approvalStatus !== statusFilter)
      return false;
    if (
      channelFilter !== "ALL" &&
      normalizeChannel(msg.communicationChannel) !==
        normalizeChannel(channelFilter)
    )
      return false;
    return true;
  });

  const approvedCount = messages.filter(
    (m) => m.approvalStatus === "APPROVED",
  ).length;

  const getSophisticationClass = (level) => {
    if (level <= 3) return "level-low";
    if (level <= 6) return "level-medium";
    return "level-high";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <div className="approval-dashboard">
      {error && <div className="error-message">{error}</div>}

      {/* ── Batch List ── */}
      <div className="batch-list">
        <div className="batch-list-header">
          <h3>Generation Batches</h3>
          <div className="batch-filter">
            <select
              value={channelFilter}
              onChange={(e) => {
                setChannelFilter(e.target.value);
                setSelectedBatch(null);
              }}
            >
              <option value="ALL">All Channels</option>
              <option value="EMAIL">Email</option>
              <option value="SMS">SMS</option>
              <option value="VOICE">Voice</option>
              <option value="SOCIAL_MEDIA">Social Media</option>
            </select>
          </div>
        </div>

        {batches.length === 0 ? (
          <div className="empty-state">
            <p>No generation batches yet. Generate some messages first.</p>
          </div>
        ) : (
          batches
            .filter(
              (b) =>
                channelFilter === "ALL" ||
                normalizeChannel(b.channel) === normalizeChannel(channelFilter),
            )
            .map((batch) => (
              <div
                key={batch.batchId}
                className={`batch-row ${selectedBatch === batch.batchId ? "selected" : ""}`}
                onClick={() =>
                  setSelectedBatch(
                    selectedBatch === batch.batchId ? null : batch.batchId,
                  )
                }
              >
                <div className="batch-info">
                  <span className="batch-date">
                    {formatDate(batch.createdAt)}
                  </span>
                  <span
                    className={`channel-badge ${batch.channel?.toLowerCase()}`}
                  >
                    {batch.channel}
                  </span>
                  <div className="batch-counts">
                    {batch.pendingCount > 0 && (
                      <span className="batch-count-badge pending">
                        {batch.pendingCount} pending
                      </span>
                    )}
                    {batch.approvedCount > 0 && (
                      <span className="batch-count-badge approved">
                        {batch.approvedCount} approved
                      </span>
                    )}
                    {batch.rejectedCount > 0 && (
                      <span className="batch-count-badge rejected">
                        {batch.rejectedCount} rejected
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className="batch-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  {batch.pendingCount > 0 && (
                    <>
                      <button
                        className="batch-approve-btn"
                        onClick={() => handleApproveBatch(batch.batchId)}
                      >
                        Approve All
                      </button>
                      <button
                        className="batch-reject-btn"
                        onClick={() => handleRejectBatch(batch.batchId)}
                      >
                        Reject All
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
        )}
      </div>

      {/* ── Message Table ── */}
      <div className="message-table-container">
        <div className="message-table-toolbar">
          <div className="message-filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>

            {approvedCount > 0 && (
              <button
                className={`clear-approved-btn ${hideApproved ? "showing" : ""}`}
                onClick={() => setHideApproved(!hideApproved)}
              >
                {hideApproved
                  ? `Show Approved (${approvedCount})`
                  : `Clear Approved (${approvedCount})`}
              </button>
            )}
          </div>

          {selectedIds.size > 0 && (
            <div className="bulk-actions">
              <button className="bulk-approve-btn" onClick={handleBulkApprove}>
                Approve Selected ({selectedIds.size})
              </button>
              <button className="bulk-reject-btn" onClick={handleBulkReject}>
                Reject Selected ({selectedIds.size})
              </button>
              <button className="bulk-delete-btn" onClick={handleBulkDelete}>
                Delete Selected ({selectedIds.size})
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <LoadingSpinner message="Loading messages..." />
        ) : filteredMessages.length === 0 ? (
          <div className="empty-state">
            <p>No messages found matching the current filters.</p>
          </div>
        ) : (
          <table className="message-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === filteredMessages.length &&
                      filteredMessages.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Subject</th>
                <th>Channel</th>
                <th>Level</th>
                <th>Context</th>
                <th>Period</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMessages.map((msg) => (
                <React.Fragment key={msg.uuid}>
                  <tr
                    className={expandedId === msg.uuid ? "expanded" : ""}
                    onClick={() =>
                      setExpandedId(expandedId === msg.uuid ? null : msg.uuid)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(msg.uuid)}
                        onChange={() => toggleSelect(msg.uuid)}
                      />
                    </td>
                    <td>
                      {msg.subject
                        ? msg.subject.length > 50
                          ? msg.subject.substring(0, 50) + "..."
                          : msg.subject
                        : "(no subject)"}
                    </td>
                    <td>
                      <span
                        className={`channel-badge ${msg.communicationChannel?.toLowerCase()}`}
                      >
                        {msg.communicationChannel}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`sophistication-badge ${getSophisticationClass(msg.sophisticationLevel)}`}
                      >
                        {msg.sophisticationLevel}
                      </span>
                    </td>
                    <td>
                      <div className="context-tags">
                        {msg.communicationContext?.split(",").map((ctx, i) => (
                          <span key={i} className="context-tag">
                            {ctx.trim()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`period-badge ${(msg.activePeriodType || "ALWAYS").toLowerCase()}`}
                      >
                        {msg.activePeriodType || "ALWAYS"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${msg.approvalStatus?.toLowerCase()}`}
                      >
                        {msg.approvalStatus}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        {msg.approvalStatus === "PENDING" && (
                          <>
                            <button
                              className="approve-btn"
                              title="Approve"
                              onClick={() => handleApprove(msg.uuid)}
                            >
                              &#10003;
                            </button>
                            <button
                              className="reject-btn"
                              title="Reject"
                              onClick={() => {
                                setRejectingId(msg.uuid);
                                setRejectReason("");
                              }}
                            >
                              &#10007;
                            </button>
                            <button
                              className="edit-btn"
                              title="Edit"
                              onClick={() => setEditingMessage(msg)}
                            >
                              &#9998;
                            </button>
                          </>
                        )}
                      </div>
                      {rejectingId === msg.uuid && (
                        <div className="reject-inline">
                          <input
                            type="text"
                            placeholder="Reason (optional)"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <button
                            className="reject-confirm"
                            onClick={() => handleReject(msg.uuid)}
                          >
                            Reject
                          </button>
                          <button
                            className="reject-cancel"
                            onClick={() => setRejectingId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Preview Row */}
                  {expandedId === msg.uuid && (
                    <tr>
                      <td colSpan="8" style={{ padding: 0 }}>
                        <div className="message-preview">
                          <div className="preview-content">
                            {msg.contentHtml && (
                              <div className="preview-section">
                                <h4>HTML Preview</h4>
                                <div
                                  className="html-preview"
                                  dangerouslySetInnerHTML={{
                                    __html: msg.contentHtml,
                                  }}
                                />
                              </div>
                            )}
                            <div className="preview-section">
                              <h4>Plain Text</h4>
                              <div className="plain-text-preview">
                                {msg.contentPlainText}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Message Editor Modal ── */}
      {editingMessage && (
        <MessageEditor
          message={editingMessage}
          onSave={handleSaveEdit}
          onClose={() => setEditingMessage(null)}
        />
      )}
    </div>
  );
};

export default ApprovalDashboard;
